/**
 * PhoneOtpForm — reusable two-step SMS phone-number login widget.
 *
 *   Step 1: enter phone → onSend(phone)
 *   Step 2: enter the 5-digit code → onVerify(phone, code, name?)
 *
 * Both callbacks return { success, error?, retryAfter?, ... } (the shape the
 * AuthContext OTP helpers already return). The parent decides what success
 * does (close modal, navigate, etc).
 *
 * Props:
 *   onSend(phone)            required — request a code
 *   onVerify(phone, code, name) required — verify the code
 *   askName   (bool)         show an optional name field on step 2 (customer signup)
 *   accent    (string)       accent colour for buttons
 *   labels    (object)       optional text overrides
 */
import React, { useState, useEffect, useRef } from 'react';
import { COUNTRIES, DEFAULT_COUNTRY, findByIso } from '../data/countryCodes';
import { geoApi } from '../api/apiClient';

// Built-in OTP-form labels per language; the form auto-picks one from the
// active <html lang>, so every caller (customer + business + platform) gets a
// translated phone form without passing anything. Explicit `labels` override.

// Build a `labels` object from a translator. `t` may be the CoreLang `T` object
// (otpPhoneLabel…) or the customer `tu` function (tu('otp_phone_label')). Only
// defined values are returned so they never blank out the built-in fallback.
export function buildOtpLabels(t) {
  const get = typeof t === 'function'
    ? (objKey, tuKey) => { const v = t(tuKey); return v && v !== tuKey ? v : undefined; }
    : (objKey) => (t && t[objKey]) || undefined;
  const out = {
    phoneLabel:  get('otpPhoneLabel',  'otp_phone_label'),
    sendBtn:     get('otpSendBtn',     'otp_send_btn'),
    codeLabel:   get('otpCodeLabel',   'otp_code_label'),
    verifyBtn:   get('otpVerifyBtn',   'otp_verify_btn'),
    nameLabel:   get('otpNameLabel',   'otp_name_label'),
    namePh:      get('otpNamePh',      'otp_name_ph'),
    changePhone: get('otpChangePhone', 'otp_change_phone'),
    resend:      get('otpResend',      'otp_resend'),
    sentTo:      get('otpSentTo',      'otp_sent_to'),
  };
  Object.keys(out).forEach(k => out[k] === undefined && delete out[k]);
  return out;
}

const OTP_I18N = {
  en: { phoneLabel:'Mobile number', phonePh:'09xx xxx xxxx', sendBtn:'Send code', codeLabel:'Verification code', codePh:'• • • • •', verifyBtn:'Confirm & continue', nameLabel:'Your name (optional)', namePh:'e.g. Sara', changePhone:'Change number', resend:'Resend code', sentTo:'Code sent to' },
  fa: { phoneLabel:'شماره موبایل', phonePh:'09xx xxx xxxx', sendBtn:'ارسال کد', codeLabel:'کد تأیید', codePh:'• • • • •', verifyBtn:'تأیید و ادامه', nameLabel:'نام شما (اختیاری)', namePh:'مثلاً سارا', changePhone:'تغییر شماره', resend:'ارسال مجدد کد', sentTo:'کد به این شماره ارسال شد' },
  ar: { phoneLabel:'رقم الجوال', phonePh:'09xx xxx xxxx', sendBtn:'إرسال الرمز', codeLabel:'رمز التحقق', codePh:'• • • • •', verifyBtn:'تأكيد ومتابعة', nameLabel:'اسمك (اختياري)', namePh:'مثال: سارة', changePhone:'تغيير الرقم', resend:'إعادة إرسال الرمز', sentTo:'تم إرسال الرمز إلى' },
};
export default function PhoneOtpForm({ onSend, onVerify, askName = false, accent = '#c8a96e', labels = {}, lang }) {
  const activeLang = lang || (typeof document !== 'undefined' ? (document.documentElement.getAttribute('lang') || 'en') : 'en');
  const L = { ...(OTP_I18N[activeLang] || OTP_I18N.en), ...labels };

  const [step,    setStep]    = useState('phone'); // 'phone' | 'code'
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [phone,   setPhone]   = useState('');
  const [code,    setCode]    = useState('');
  const [name,    setName]    = useState('');
  const [error,   setError]   = useState('');
  const [info,    setInfo]    = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const codeRef = useRef(null);

  // Full international number to send (dial code + local digits, no leading 0).
  const fullPhone = () => {
    const local = phone.replace(/[^\d]/g, '').replace(/^0+/, '');
    return `${country.dial}${local}`;
  };

  // Preselect the country from the caller's IP (Telegram-style default).
  useEffect(() => {
    let alive = true;
    geoApi.country()
      .then(r => { const c = findByIso(r?.country); if (alive && c) setCountry(c); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => { if (step === 'code') setTimeout(() => codeRef.current?.focus(), 80); }, [step]);

  const doSend = async () => {
    if (!phone.trim() || loading) return;
    setLoading(true); setError(''); setInfo('');
    const res = await onSend(fullPhone());
    setLoading(false);
    if (res.success) {
      setStep('code');
      setCooldown(res.retryAfter || 60);
      // Development mode: the server returns the code so we can show it on screen.
      if (res.devCode) { setCode(String(res.devCode)); setInfo(`🛠️ Test mode — your code is ${res.devCode}`); }
      else if (res.mock) setInfo('Test mode: check the server console for your code.');
      else setInfo(`${L.sentTo} ${fullPhone()}`);
    } else {
      setError(res.error || 'Could not send the code');
      if (res.retryAfter) setCooldown(res.retryAfter);
    }
  };

  const doResend = async () => {
    if (cooldown > 0 || loading) return;
    await doSend();
  };

  const doVerify = async () => {
    if (!code.trim() || loading) return;
    setLoading(true); setError('');
    const res = await onVerify(fullPhone(), code.trim(), name.trim());
    setLoading(false);
    if (!res.success) setError(res.error || 'Incorrect code');
    // success is handled by the parent (modal close / navigation)
  };

  return (
    <div className="otp-form" style={{ '--otp-accent': accent }}>
      {error && <div className="otp-error">{error}</div>}
      {info && !error && <div className="otp-info">{info}</div>}

      {step === 'phone' ? (
        <>
          <label className="otp-label">{L.phoneLabel}</label>
          <div className="otp-phone-row">
            <select
              className="otp-cc"
              value={country.iso}
              onChange={e => { const c = COUNTRIES.find(x => x.iso === e.target.value); if (c) setCountry(c); }}
              title={country.name}
            >
              {COUNTRIES.map(c => (
                <option key={c.iso} value={c.iso}>{c.flag} {c.dial}</option>
              ))}
            </select>
            <input
              className="otp-input otp-phone-input" inputMode="tel" autoFocus dir="ltr"
              value={phone} onChange={e => setPhone(e.target.value)}
              placeholder={country.iso === 'IR' ? '912 345 6789' : '...'}
              onKeyDown={e => e.key === 'Enter' && doSend()}
            />
          </div>
          <button className="otp-btn" onClick={doSend} disabled={loading || !phone.trim()}>
            {loading ? '…' : L.sendBtn}
          </button>
        </>
      ) : (
        <>
          {askName && (
            <>
              <label className="otp-label">{L.nameLabel}</label>
              <input className="otp-input" value={name} onChange={e => setName(e.target.value)} placeholder={L.namePh} />
            </>
          )}
          <label className="otp-label">{L.codeLabel}</label>
          <input
            ref={codeRef} className="otp-input otp-code" inputMode="numeric" maxLength={6} dir="ltr"
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder={L.codePh}
            onKeyDown={e => e.key === 'Enter' && doVerify()}
          />
          <button className="otp-btn" onClick={doVerify} disabled={loading || !code.trim()}>
            {loading ? '…' : L.verifyBtn}
          </button>
          <div className="otp-actions">
            <button className="otp-link" onClick={() => { setStep('phone'); setCode(''); setError(''); setInfo(''); }}>{L.changePhone}</button>
            <button className="otp-link" onClick={doResend} disabled={cooldown > 0}>
              {cooldown > 0 ? `${L.resend} (${cooldown}s)` : L.resend}
            </button>
          </div>
        </>
      )}

      <style>{`
        .otp-form { display:flex; flex-direction:column; gap:.6rem; }
        .otp-label { font-size:.72rem; color:rgba(255,255,255,.45); text-transform:uppercase; letter-spacing:.07em; }
        .otp-input { background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); border-radius:10px; padding:.8rem 1rem; color:#fff; font-family:'DM Sans',sans-serif; font-size:.95rem; outline:none; transition:border-color .2s; width:100%; box-sizing:border-box; }
        .otp-input:focus { border-color:var(--otp-accent); }
        /* Phone numbers + OTP codes are always LTR, even inside RTL (fa/ar) UIs */
        .otp-phone-row { display:flex; gap:.5rem; direction:ltr; }
        .otp-code { direction:ltr; text-align:left; }
        .otp-cc { flex:0 0 auto; max-width:120px; background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); border-radius:10px; padding:.8rem .5rem; color:#fff; font-family:'DM Sans',sans-serif; font-size:.9rem; outline:none; cursor:pointer; }
        .otp-cc:focus { border-color:var(--otp-accent); }
        .otp-cc option { background:#1a1a1a; color:#fff; }
        .otp-phone-input { flex:1; min-width:0; }
        .otp-code { letter-spacing:.4em; text-align:center; font-size:1.3rem; font-weight:700; }
        .otp-btn { width:100%; padding:.9rem; border:none; border-radius:12px; background:var(--otp-accent); color:#fff; font-family:'DM Sans',sans-serif; font-size:.95rem; font-weight:700; cursor:pointer; transition:opacity .2s; }
        .otp-btn:hover:not(:disabled) { opacity:.88; }
        .otp-btn:disabled { opacity:.5; cursor:not-allowed; }
        .otp-actions { display:flex; justify-content:space-between; margin-top:.15rem; }
        .otp-link { background:none; border:none; color:rgba(255,255,255,.5); font-family:'DM Sans',sans-serif; font-size:.78rem; cursor:pointer; padding:.2rem; }
        .otp-link:hover:not(:disabled) { color:#fff; }
        .otp-link:disabled { opacity:.4; cursor:not-allowed; }
        .otp-error { background:rgba(239,68,68,.1); border:1px solid rgba(239,68,68,.3); border-radius:10px; padding:.6rem .9rem; color:#fca5a5; font-size:.82rem; }
        .otp-info  { background:rgba(52,211,153,.08); border:1px solid rgba(52,211,153,.25); border-radius:10px; padding:.6rem .9rem; color:#6ee7b7; font-size:.8rem; }
      `}</style>
    </div>
  );
}
